import classnames from 'classnames'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  APP_BANNER_ANIMATION_DURATION_MS,
  APP_BANNER_DISMISS_TICK_EVENT,
  isAppBannerScrollDriven,
} from '@src/components/app-push-banner/app-push-banner.utils'
import { useStickyWrapperHeight } from '@src/hooks/useStickyWrapperHeight'
import { Skeleton } from '@src/quince-components/src'
import Image from '@src/quince-components/src/atoms/image'
import Link from '@src/quince-components/src/atoms/link'
import { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { useOptimisticPaint } from '@src/quince-site-common/src/contexts/responsive-interactions-context/responsive-interactions-context'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import { useResettableOptimisticValue } from '@src/quince-site-common/src/hooks/use-optimistic-selection'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import GridSlider from '@src/SectionRenderer/sections/gridSlider'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { isModifiedClickEvent } from '@src/utils/click-event.utils'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { ALL_SUBCOLLECTION_FILTER_ID } from '../../collection.constants'
import { SubCollection } from '../../domains/Collection'

import { SubCollectionCardListProps } from './sub-collection-card-list.types'
import styles from './subCollectionCardList.module.scss'

const ALL_SUBCOLLECTION_TITLE = 'all_subcollection'
const SUB_COLLECTION_CARD_TITLE_DATA_TYPE = 'sub-collection-card-title'
const SUB_COLLECTION_LINK_SELECTOR_KEY = 'collection-subcollection-link'

const SubCollectionCardList = ({
  subCollections = [],
  className,
  onCardClick,
  isSubCollectionSelected,
  collectionExploreEverythingImage,
  contentful_id,
  showLoader,
  trackSubcollectionCardClick = true,
}: SubCollectionCardListProps) => {
  const optimisticPaint = useOptimisticPaint()
  const path = usePathname()
  const deviceInfo = useDeviceInfo()
  const isNewLocalisedFlow = useI18nPreviewContext()
  const { t } = useTranslation('common')

  const [headerHeight, setHeaderHeight] = useState(0)

  const [intersectRef, isNotScrolledDown] = useIntersect(null, '0px', 0, true) as unknown as [
    React.RefObject<HTMLDivElement>,
    boolean,
  ]

  const isScrolledDown = !isNotScrolledDown
  const globalIsScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)
  const showMobileMenu = useAppSelector<boolean>(siteStateManager, siteStateManager.getShowMobileMenu)
  const isSearchMode = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsSearchMode)

  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = !isDesktop

  // Create "All" subcollection if image is provided
  const allSubCollections = useMemo(() => {
    if (collectionExploreEverythingImage && subCollections.length > 1) {
      // Convert the contentful image to the format expected by SubCollection
      const imageDesktop = new ImageDomain({
        id: collectionExploreEverythingImage.id,
        title: collectionExploreEverythingImage.title,
        description: collectionExploreEverythingImage.description,
        url: collectionExploreEverythingImage.file.url,
        fileName: collectionExploreEverythingImage.file.fileName,
        height: collectionExploreEverythingImage.file.details.image.height,
        width: collectionExploreEverythingImage.file.details.image.width,
        loading: 'eager',
        displayModelInfo: false,
      })

      const allOption = new SubCollection({
        id: ALL_SUBCOLLECTION_FILTER_ID,
        title: t(ALL_SUBCOLLECTION_TITLE),
        imageDesktop,
        slug: path.split('/').slice(0, -1).join('/') || path, // Parent path or current path if no parent
      })

      return [allOption, ...subCollections]
    }

    return subCollections
  }, [subCollections, collectionExploreEverythingImage, path, t])

  const selectedSubcollectionIds = useMemo(() => {
    if (!isSubCollectionSelected) return []
    return allSubCollections
      .slice(1)
      .filter(sc => isSubCollectionSelected(sc))
      .map(sc => String(sc.id))
  }, [allSubCollections, isSubCollectionSelected])

  const committedSelectionKey = isSubCollectionSelected
    ? selectedSubcollectionIds.join('|') || ALL_SUBCOLLECTION_FILTER_ID
    : path

  // Optimistic paint: single-value id overlay (string = a card id is optimistically selected;
  // ALL_SUBCOLLECTION_FILTER_ID = "none selected" paint; undefined = no override, use committed state).
  const { optimisticValue: optimisticId, setOptimisticValueAndDeferCommit } = useResettableOptimisticValue<string>(
    committedSelectionKey,
    optimisticPaint,
  )

  const hasSlider = !isDesktop || allSubCollections.length > 9

  const classes = classnames(
    styles.container,
    className,
    !hasSlider ? styles.container__flat : undefined,
    hasSlider ? styles.container__slider : undefined,
    styles.newSubCollectionFilter,
  )

  const imageMaxWidth = getMaxWidthInPx({
    maxWidth: styles.subCollectionCardImageWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const { wrapperRef, containerRef } = useStickyWrapperHeight({
    enabled: isMobile,
    dependencies: [allSubCollections.length],
  })
  // Get header height when component mounts and when scrolling direction changes
  useEffect(() => {
    if (!isBrowser()) {
      return
    }
    // Treatment only: the scroll-driven banner is a fixed overlay and the header is offset below it, so
    // measure the header's rendered `.bottom` (includes that offset) and re-measure across the animation
    // window. Control reproduces prod: the banner is in-flow (sticky), so anchor to the header height
    // with a single measurement + resize listener.
    const scrollDriven = isAppBannerScrollDriven()

    const getHeaderHeight = () => {
      const subCategoryListElement = document.getElementById('sub-category-list-header')
      const subCategoryListHeight = subCategoryListElement?.getBoundingClientRect().height

      const headerElement = document.getElementById('site-primary-header')
      const headerRect = headerElement?.getBoundingClientRect()
      const headerMetric = scrollDriven ? (headerRect?.bottom ?? 0) : (headerRect?.height ?? 0)
      setHeaderHeight(Math.max(0, headerMetric) + (subCategoryListHeight ?? 0))
    }

    // Control: prod parity — a single measurement + resize listener, no animation tracking.
    if (!scrollDriven) {
      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight)
      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }

    // On scroll UP the nav bar SLIDES back in over the header motion token (300ms), so its rendered
    // `.bottom` isn't final on the frame the scroll direction flips — a single read here captured the
    // nav's mid-slide (hidden) bottom and locked the sticky subcollection to it, leaving the chips
    // overlapping the still-animating (clipped) nav. Re-measure every frame across the animation window
    // so the subcollection tracks the nav down to its settled bottom.
    let rafId = 0
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const settleLoop = () => {
      getHeaderHeight()
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      if (now - start < APP_BANNER_ANIMATION_DURATION_MS + 60) {
        rafId = window.requestAnimationFrame(settleLoop)
      }
    }
    // Measure immediately (no one-frame gap on mount / direction flip), then keep re-measuring
    // across the animation window; settleLoop self-schedules the follow-up frames.
    settleLoop()
    window.addEventListener('resize', getHeaderHeight)
    // Track the nav's animated bottom during the app-banner dismiss slide via the scoped tick (instead
    // of a page-wide resize storm).
    window.addEventListener(APP_BANNER_DISMISS_TICK_EVENT, getHeaderHeight)
    // Track the header's bottom every scroll frame: the scroll-UP app-banner exit collapses the nav's
    // below-banner offset per scroll position (not on a timer), so the sticky subcollection must follow
    // the nav's shrinking bottom the whole way up — the settleLoop only covers the animation window
    // after a direction flip, which a slow traversal of the exit zone would fall outside of.
    let scrollRaf = 0
    const onScroll = () => {
      if (scrollRaf) return
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0
        getHeaderHeight()
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.cancelAnimationFrame(rafId)
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf)
      window.removeEventListener('resize', getHeaderHeight)
      window.removeEventListener(APP_BANNER_DISMISS_TICK_EVENT, getHeaderHeight)
      window.removeEventListener('scroll', onScroll)
    }
  }, [globalIsScrollingDown])

  const handleCardClick = useCallback(
    (subCollection: SubCollection) => {
      if (trackSubcollectionCardClick) {
        trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK, {
          slug: subCollection.slug,
          title: subCollection.title,
        })
      }
      onCardClick(subCollection)
    },
    [onCardClick, trackSubcollectionCardClick],
  )

  // Check if any subcollection filter is selected
  const hasAnySubcollectionSelected = useCallback(() => {
    return selectedSubcollectionIds.length > 0
  }, [selectedSubcollectionIds])

  // Determine if a subcollection is selected (committed state only — no optimistic overlay).
  const isSubcollectionSelectedWithAll = useCallback(
    (subCollection: SubCollection) => {
      if (!isSubCollectionSelected) return path === subCollection.slug

      // If it's the "All" card (first one)
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        // "All" is selected when no other subcollection is selected
        return !hasAnySubcollectionSelected()
      }
      // For other subcollections, use the original logic
      return isSubCollectionSelected(subCollection)
    },
    [isSubCollectionSelected, path, hasAnySubcollectionSelected],
  )

  // Effective selected state: optimistic overlay layered over committed state.
  // When `optimisticPaint` is off or no optimistic pick is live, falls back to committed state
  // (byte-for-byte identical to pre-optimization behavior).
  const effectiveIsSelected = useCallback(
    (subCollection: SubCollection): boolean => {
      if (!optimisticPaint || optimisticId === undefined) {
        return isSubcollectionSelectedWithAll(subCollection)
      }
      // Overlay is live: paint the desired state.
      // optimisticId === ALL_SUBCOLLECTION_FILTER_ID means "none selected" (paint All-selected, others off).
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        return optimisticId === ALL_SUBCOLLECTION_FILTER_ID
      }
      return String(subCollection.id) === optimisticId
    },
    [optimisticPaint, optimisticId, isSubcollectionSelectedWithAll],
  )

  // Custom click handler to handle the "All" card differently
  const handleSubcollectionClick = useCallback(
    (subCollection: SubCollection) => {
      // If this is the "All" option
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        // If any other filters are selected, reset them all
        if (hasAnySubcollectionSelected()) {
          if (optimisticPaint) {
            // Paint "none selected" (All card highlighted, all others off) immediately.
            setOptimisticValueAndDeferCommit(ALL_SUBCOLLECTION_FILTER_ID, () => {
              onCardClick(subCollection)
            })
          } else {
            // Reset all filters by calling onCardClick with the "All" option
            onCardClick(subCollection)
          }
        }
        // If "All" is already selected, do nothing
        return
      }
      // For normal subcollections, use the optimistic or direct click handler
      if (optimisticPaint) {
        // Determine desired paint: if this card is already the effective selected card, paint
        // "none selected" (toggle-off to All). Otherwise paint this card as selected.
        const isCurrentlyEffectivelySelected = effectiveIsSelected(subCollection)
        const desiredId = isCurrentlyEffectivelySelected ? ALL_SUBCOLLECTION_FILTER_ID : String(subCollection.id)
        setOptimisticValueAndDeferCommit(desiredId, () => {
          handleCardClick(subCollection)
        })
      } else {
        handleCardClick(subCollection)
      }
    },
    [
      handleCardClick,
      hasAnySubcollectionSelected,
      onCardClick,
      optimisticPaint,
      effectiveIsSelected,
      setOptimisticValueAndDeferCommit,
    ],
  )

  const renderCard = (subCollection: SubCollection, index: number) => {
    const imageProps = ImageDomain.makeImageProps(subCollection.imageDesktop)

    const isSelected = effectiveIsSelected(subCollection)

    const cardClasses = classnames(
      styles.subCollectionCard,
      styles.newSubCollectionCardFilter,
      index === 0 ? styles.firstCardExperiment : undefined,
      index === allSubCollections.length - 1 ? styles.lastCardExperiment : undefined,
      !hasSlider ? styles.subCollectionCard__withBorder : undefined,
      isSelected ? styles.subCollectionCard__selected : undefined,
      !hasSlider && isSelected ? styles.subCollectionCard__selected__flat : undefined,
      index === allSubCollections.length - 1 ? styles.subCollectionCard__lastCard : undefined,
      !isDesktop ? styles.newSubCollectionCardFilter : undefined,
    )

    const imageElement = isDesktop ? (
      <Image
        alt={imageProps.alt}
        {...imageProps}
        className={styles.subCollectionCard__image__newSubCollectionCardFilter}
        maxWidth={imageMaxWidth}
        loading="eager"
      />
    ) : null

    const titleElement = (
      <h2
        className={classnames(styles.subCollectionCard__text, styles.subCollectionCard__text__newFilter)}
        data-content-type={SUB_COLLECTION_CARD_TITLE_DATA_TYPE}
        data-slug={subCollection.slug}
        data-active={isSelected}
      >
        {getDisplayKey(subCollection, 'displayTitle', subCollection.title, isNewLocalisedFlow)}
      </h2>
    )

    return (
      <Link
        className={cardClasses}
        key={subCollection.id}
        data-testid="subcollection-card"
        selectorKey={SUB_COLLECTION_LINK_SELECTOR_KEY}
        // Falsy check (not ??): consumers in query mode pass slug: '' — never render href="".
        href={subCollection.slug ? subCollection.slug : undefined}
        onClick={(event: React.MouseEvent) => {
          // Modified clicks (new tab/window, middle-click) follow the crawlable href;
          // plain clicks keep the in-app filter/toggle navigation.
          if (isModifiedClickEvent(event)) return
          event.preventDefault()
          handleSubcollectionClick(subCollection)
        }}
      >
        {isDesktop ? (
          <>
            {imageElement}
            {titleElement}
          </>
        ) : (
          <>
            {titleElement}
            {imageElement}
          </>
        )}
      </Link>
    )
  }

  const renderCards = () => {
    return allSubCollections.map((subCollection, index) => renderCard(subCollection, index))
  }

  const cardContent = renderCards()

  if (showLoader && subCollections.length > 0) {
    return (
      <div className={styles.subCollectionCardLoader}>
        {new Array(8).fill(0).map((_, index) => (
          <div key={index} className={styles.subCollectionCardLoaderItem}>
            <Skeleton className={styles.subCollectionCardLoaderImage} />
            <Skeleton className={styles.subCollectionCardLoaderTitle} />
          </div>
        ))}
      </div>
    )
  }

  // Separate sticky state from active state to prevent CLS
  const shouldBeSticky = isMobile && isScrolledDown
  const isSticky = shouldBeSticky && !globalIsScrollingDown

  const shouldHideSubcollection = isMobile && (showMobileMenu || isSearchMode)
  const getInlineStyles = (): React.CSSProperties | undefined => {
    if (!isDesktop && isSticky) {
      return { top: `${headerHeight}px` }
    }
    return undefined
  }

  return (
    <div className={styles.subCollectionCardList}>
      <div
        ref={wrapperRef}
        className={classnames(styles.subcollection_wrapper, {
          [`${styles.new_subcollection_filter_wrapper}`]: isMobile,
        })}
      >
        {hasSlider ? (
          <div
            ref={containerRef}
            className={classnames(
              isSticky ? styles.fixedSubcollectionControl : '',
              !isDesktop && shouldBeSticky ? styles.fixedSubcollectionExperiment : '',
              shouldHideSubcollection ? styles.hiddenDuringMobileMenu : '',
            )}
            style={getInlineStyles()}
            id="subcollection-card-list"
          >
            <GridSlider
              navPosition={NAV_POSITION.LEFT_RIGHT}
              hideBorderTop
              hideCenterLine
              className={classes}
              swiperStyles={classnames(styles.swiperStyles, styles.hideBorder, styles.newCLPFiltersSwiperStyles)}
              slideClassName={classnames(styles.slideClassName, styles.slideClassName__newCLPFiltersSlide)}
              prevIconClass={classnames(
                styles.iconSize,
                styles.prevIcon,
                styles.iconSize__circle,
                styles.iconSize__circle__prevIcon,
              )}
              nextIconClass={classnames(styles.iconSize, styles.iconSize__circle, styles.iconSize__circle__nextIcon)}
              speed={isMobile ? 200 : undefined}
              swiperId={`subcollection_card_list-${contentful_id}`}
              isChevronIcon={true}
              isIconWithBG={true}
              id={`subcollection_card_list-${contentful_id}`}
            >
              {cardContent}
            </GridSlider>
          </div>
        ) : (
          <div
            ref={containerRef}
            className={classnames(
              classes,
              isSticky ? styles.fixedSubcollectionControl : '',
              shouldHideSubcollection ? styles.hiddenDuringMobileMenu : '',
            )}
            id="subcollection-card-list"
            style={getInlineStyles()}
          >
            {cardContent}
          </div>
        )}
      </div>
      <div ref={intersectRef}></div>
    </div>
  )
}

export default SubCollectionCardList
